import { EditableContent } from '../../edit/EditableContent';
import { EditableMedia } from '../../edit/EditableMedia';
import { LucideIcon } from '../../icons/LucideIcon';
import { resolveSectionLucideIcon } from '../../icons/ctaLucideMap';
import { buildSiteImageProxyUrl, buildSiteImageSrcSet } from '../../lib/imageProxy';
import { SITE_IMAGE_SIZES } from '../../lib/responsiveImageSizes';
import { safeMapEmbedUrl } from '../../lib/safeGuestLinks';
import { pickBlockField } from '../../lib/localized';
import { pickMediaUrl } from '../../lib/media';
import type { BlockRenderProps } from '../../types';

export function AddressMapBlock({
  block,
  locale,
  isActive,
  activeEditTarget,
  editable,
}: BlockRenderProps) {
  const sectionTitle = pickBlockField(block.content, 'sectionTitle', locale);
  const sectionIconKey = pickBlockField(block.content, 'sectionIcon', locale);
  const address = pickBlockField(block.content, 'address', locale);
  const directions = pickBlockField(block.content, 'directions', locale);
  const mapEmbedUrl = safeMapEmbedUrl(pickBlockField(block.content, 'mapEmbedUrl', locale));
  const mapImage = pickMediaUrl(block, 'mapImage');
  const mapImageSrc = mapImage ? buildSiteImageProxyUrl(mapImage, 1200, 78) : undefined;
  const mapImageSrcSet = mapImage
    ? buildSiteImageSrcSet(mapImage, [480, 768, 1200], 78)
    : undefined;
  const target = isActive ? activeEditTarget : null;
  const sectionIcon = resolveSectionLucideIcon(sectionIconKey, 'location');

  return (
    <section
      className={['ws-address-map', isActive ? 'ws-block--active' : ''].filter(Boolean).join(' ')}
      data-block-id={block.blockId}
      data-component={block.componentType}
      id="map"
    >
      <div className="ws-address-map__inner">
        <div className="ws-address-map__copy">
          <div className="ws-address-map__header">
            <EditableContent
              as="span"
              fieldKey="sectionIcon"
              editable={editable}
              activeEditTarget={target}
              className="ws-address-map__icon-hit"
            >
              <LucideIcon icon={sectionIcon} size="md" className="ws-address-map__icon" />
            </EditableContent>
            <EditableContent
              as="h2"
              fieldKey="sectionTitle"
              editable={editable}
              activeEditTarget={target}
              className="ws-address-map__title"
            >
              {sectionTitle}
            </EditableContent>
          </div>
          <EditableContent
            fieldKey="address"
            editable={editable}
            activeEditTarget={target}
            className="ws-address-map__address"
          >
            {address}
          </EditableContent>
          {directions || editable ? (
            <EditableContent
              fieldKey="directions"
              editable={editable}
              activeEditTarget={target}
              className="ws-address-map__directions"
            >
              {directions}
            </EditableContent>
          ) : null}
        </div>
        <div className="ws-address-map__visual">
          {mapEmbedUrl ? (
            <iframe
              className="ws-address-map__embed"
              src={mapEmbedUrl}
              title={sectionTitle || 'Map'}
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
              allowFullScreen
            />
          ) : mapImageSrc ? (
            <EditableMedia
              fieldKey="mapImage"
              editable={editable}
              activeEditTarget={target}
              className="ws-address-map__photo"
              src={mapImageSrc}
              srcSet={mapImageSrcSet}
              sizes={SITE_IMAGE_SIZES.menuBanner}
              alt={address || sectionTitle || 'Map'}
              width={1200}
              height={800}
              loading="lazy"
              decoding="async"
            />
          ) : (
            <div className="ws-address-map__placeholder" aria-hidden />
          )}
        </div>
      </div>
    </section>
  );
}
